/**
 * engines/civilization
 *
 * カルダシェフ・スケール（文明のエネルギー消費量による分類）と、
 * 指数関数的なエネルギー消費成長モデルを扱う。
 *
 * 【この engine の性質 — 2種類の異なる確からしさが混在する】
 * - kardashevScale() は「現在の消費電力からKの値を求める」だけの純粋な単位換算・
 *   対数計算であり、入力（現在の文明の総消費電力）さえ正しければ検証可能。
 *   実際、標準的な仮定(全世界の一次エネルギー消費量 約18.5TW)を入れると
 *   K≈0.7〜0.73となり、これは「人類はカルダシェフ・スケールでタイプ0.7程度」
 *   という、Michio Kaku 等がしばしば述べる有名な figure と一致する。
 * - 一方、projectedPower() や yearsToReachPower() は「成長率が今後も一定と
 *   仮定したら」という強い仮定に基づく外挿であり、ドレイクの方程式のfi・fc・L
 *   と同様、実測できる将来などない。年成長率をわずか1%変えるだけで、
 *   到達年数は数百年〜数千年単位で変わる。これは「予測」ではなく
 *   「この仮定なら何年かかるか」という思考実験として扱うこと。
 */

/**
 * カルダシェフ・スケールの連続値 K を求める(カール・セーガンによる定式化)。
 * K = (log10(P) − 6) / 10
 * P はワット単位の消費電力。K=1がタイプI(惑星规模、約10^16W)、
 * K=2がタイプII(恒星规模、約10^26W)、K=3がタイプIII(銀河规模、約10^36W)に相当する。
 */
export function kardashevScale(powerWatts: number): number {
  if (powerWatts <= 0) throw new Error('powerWatts must be positive');
  return (Math.log10(powerWatts) - 6) / 10;
}

/** K の値から、おおよその文明タイプの説明ラベルを返す。 */
export function kardashevTypeLabel(k: number): { ja: string; en: string } {
  if (k < 1) return { ja: `タイプ0(前段階) — K≈${k.toFixed(2)}`, en: `Type 0 (pre-planetary) — K≈${k.toFixed(2)}` };
  if (k < 2) return { ja: `タイプI に向かう途上 — K≈${k.toFixed(2)}`, en: `Approaching Type I — K≈${k.toFixed(2)}` };
  if (k < 3) return { ja: `タイプII に向かう途上 — K≈${k.toFixed(2)}`, en: `Approaching Type II — K≈${k.toFixed(2)}` };
  return { ja: `タイプIII 以上 — K≈${k.toFixed(2)}`, en: `Type III or beyond — K≈${k.toFixed(2)}` };
}

/** カルダシェフ・スケールの各タイプにあたる消費電力の目安 [W]（標準的な定義）。 */
export const KARDASHEV_THRESHOLDS_W = {
  typeI: 1e16, // 惑星に届く恒星のエネルギーをすべて利用
  typeII: 1e26, // 恒星のエネルギー出力をすべて利用（例: ダイソン球）
  typeIII: 1e36, // 銀河系のエネルギー出力をすべて利用
};

/**
 * 現在の文明の消費電力の目安 [W]（世界の一次エネルギー消費量、標準的な推定値）。
 * 出典: 各種エネルギー統計の平均的な値（年により変動するため「目安」とする）。
 */
export const CURRENT_CIVILIZATION_POWER_W = 1.85e13; // 約18.5兆ワット

/**
 * 消費電力が年率 growthRatePerYear で指数関数的に成長すると仮定したときの、
 * years年後の消費電力 [W]。
 *
 * P(t) = P0 × (1 + r)^t
 *
 * 【注意】この関数は「成長率が変わらず続いたら」という仮定の外挿であり、
 * 実際の文明の成長が指数関数的に続く保証はどこにもない。
 */
export function projectedPower(currentPowerW: number, growthRatePerYear: number, years: number): number {
  if (currentPowerW <= 0) throw new Error('currentPowerW must be positive');
  return currentPowerW * Math.pow(1 + growthRatePerYear, years);
}

/**
 * 消費電力が targetPowerW に到達するまでの年数を、現在の消費電力と一定の
 * 年成長率から逆算する。
 *
 * t = log(target/current) / log(1+r)
 */
export function yearsToReachPower(currentPowerW: number, targetPowerW: number, growthRatePerYear: number): number {
  if (currentPowerW <= 0 || targetPowerW <= 0) throw new Error('power values must be positive');
  if (growthRatePerYear <= 0) return Infinity;
  if (targetPowerW <= currentPowerW) return 0;
  return Math.log(targetPowerW / currentPowerW) / Math.log(1 + growthRatePerYear);
}
